import Phaser from 'phaser';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  ARENA_CENTER_X,
  ARENA_CENTER_Y,
  ARENA_RADIUS,
  CAT_BEYBLADE,
  CAT_ARENA_WALL,
  CAT_POWERUP,
  CLASSIC_TIMEOUT,
  DOPAMINE_TIMEOUT,
  DOPAMINE_POINTS_HIT,
  DOPAMINE_POINTS_CRITICAL,
  DOPAMINE_POINTS_CENTER_CONTROL,
  DOPAMINE_POINTS_POWERUP,
  DOPAMINE_POINTS_WALL_BOUNCE,
  DOPAMINE_POINTS_COMBO_BONUS,
  DOPAMINE_CENTER_ZONE_RADIUS,
  POWERUP_SPAWN_INTERVAL,
  POWERUP_MAX_VISIBLE,
  PLAYER_COLORS,
  BEYBLADE_CORE_RADIUS,
  IA_DIRECTION_FORCE,
  START_INVULNERABILITY_TIME,
} from '../config/Constants';
import { CLASSES } from '../data/Classes';
import { SPECIALS } from '../data/Specials';
import { POWERUPS, PowerUpDefinition } from '../data/PowerUps';
import { ARENAS, ArenaDefinition } from '../data/Arenas';
import { createEscapeMenu } from '../utils/EscapeMenu';
import { Beyblade, ActiveEffect } from '../entities/Beyblade';
import { TorneoSystem, MatchData, MatchResult } from '../systems/TorneoSystem';
import { PowerUp } from '../entities/PowerUp';
import { TorneoConfig } from './MenuScene';
import { PlayerSelection } from './SelectionScene';

interface BattlePlayer {
  beyblade: Beyblade;
  selection: PlayerSelection;
  teamId: number;
}

export class BattleScene extends Phaser.Scene {
  private config!: TorneoConfig;
  private selections!: PlayerSelection[];
  private arenaId!: string;
  private arenaDef!: ArenaDefinition;
  private torneoSystem: TorneoSystem | null = null;
  private currentMatchData: MatchData | null = null;
  private allSelections: PlayerSelection[] = [];
  private isTorneo: boolean = false;

  private players: BattlePlayer[] = [];
  private powerUps: PowerUp[] = [];
  private powerUpTimer: number = POWERUP_SPAWN_INTERVAL;
  private battleTime: number = 0;
  private battleFinished: boolean = false;
  private countdown: number = 3;
  private phase: 'countdown' | 'fighting' | 'finished' = 'countdown';
  private invulnerabilityTimer: number = 0;
  private protectionText!: Phaser.GameObjects.Text | null;
  // Estado de arena Electro
  private electroPhase: 'attract' | 'repel' = 'attract';
  private electroTimer: number = 0;
  private electroGraphics!: Phaser.GameObjects.Graphics;
  private electroLabel!: Phaser.GameObjects.Text | null;
  // Almacenar friction original de peonzas para restaurar
  private beybladeOriginalFriction: Map<Beyblade, number> = new Map();

  // UI
  private hudGraphics!: Phaser.GameObjects.Graphics;
  private timerText!: Phaser.GameObjects.Text;
  private statusTexts: Phaser.GameObjects.Text[] = [];
  private arenaGraphics!: Phaser.GameObjects.Graphics;
  private powerUpSpawnTimerText!: Phaser.GameObjects.Text;
  private rankText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'BattleScene' });
  }

  init(data: { config: TorneoConfig; selections: PlayerSelection[]; arenaId: string; torneoSystem?: TorneoSystem | null; currentMatchData?: MatchData | null; allSelections?: PlayerSelection[]; isTorneo?: boolean }): void {
    this.config = data.config;
    this.selections = data.selections;
    this.arenaId = data.arenaId;
    this.torneoSystem = data.torneoSystem || null;
    this.currentMatchData = data.currentMatchData || null;
    this.allSelections = data.allSelections || data.selections;
    this.isTorneo = data.isTorneo || false;
    this.arenaDef = ARENAS.find((a) => a.id === this.arenaId) || ARENAS[0];
    this.players = [];
    this.powerUps = [];
    this.battleTime = 0;
    this.battleFinished = false;
    this.countdown = 3;
    this.phase = 'countdown';
    this.invulnerabilityTimer = 0;
    this.protectionText = null;
    this.electroPhase = 'attract';
    this.electroTimer = 0;
    this.electroGraphics = this.add.graphics();
    this.electroLabel = null;
    this.beybladeOriginalFriction = new Map();
    this.powerUpTimer = POWERUP_SPAWN_INTERVAL;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#0a0a1a');

    // Dibujar arena
    this.arenaGraphics = this.add.graphics();
    this.drawArena();

    // Crear bordes físicos de la arena
    this.createArenaWalls();

    // Crear peonzas en posiciones iniciales
    this.createBeyblades();

    // UI
    this.createHUD();

    // Sistema de colisiones
    this.setupCollisions();

    // Escape menú
    createEscapeMenu(this,
      () => {},
      () => {
        this.cleanup();
        this.scene.start('TitleScene');
      }
    );

    // Temporizador de cuenta atrás
    this.showCountdown();

    // Inicializar timer de invulnerabilidad
    this.invulnerabilityTimer = START_INVULNERABILITY_TIME;
  }

  private drawArena(): void {
    const g = this.arenaGraphics;
    g.clear();

    const cx = ARENA_CENTER_X;
    const cy = ARENA_CENTER_Y;
    const r = ARENA_RADIUS;

    // Suelo
    const groundColor = this.getArenaGroundColor();
    g.fillStyle(groundColor, 0.2);
    g.fillCircle(cx, cy, r);

    // Borde
    const borderColor = this.getArenaBorderColor();
    g.lineStyle(6, borderColor, 0.8);
    g.strokeCircle(cx, cy, r);

    // Anillos concéntricos decorativos
    g.lineStyle(1, borderColor, 0.2);
    g.strokeCircle(cx, cy, r * 0.66);
    g.strokeCircle(cx, cy, r * 0.33);

    // Efectos de arena
    if (this.arenaDef.id === 'diana') {
      // Zonas de puntuación
      g.fillStyle(0xf1c40f, 0.1);
      g.fillCircle(cx, cy, DOPAMINE_CENTER_ZONE_RADIUS);
      g.fillStyle(0xe67e22, 0.08);
      g.fillCircle(cx, cy, DOPAMINE_CENTER_ZONE_RADIUS * 2);
    }

    // Obstáculos para laberinto
    if (this.arenaDef.hasObstacles) {
      const obstaclePositions = [
        { x: cx - 100, y: cy - 80, w: 30, h: 30 },
        { x: cx + 120, y: cy + 60, w: 25, h: 40 },
        { x: cx - 50, y: cy + 100, w: 35, h: 25 },
        { x: cx + 80, y: cy - 100, w: 28, h: 28 },
      ];

      obstaclePositions.forEach((obs) => {
        g.fillStyle(0x7f8c8d, 0.6);
        g.fillRoundedRect(obs.x - obs.w / 2, obs.y - obs.h / 2, obs.w, obs.h, 8);

        // Cuerpo físico del obstáculo
        this.matter.add.rectangle(obs.x, obs.y, obs.w, obs.h, {
          isStatic: true,
          label: 'obstacle',
          collisionFilter: {
            category: CAT_ARENA_WALL,
            mask: CAT_BEYBLADE,
          },
        });
      });
    }

    // Efectos de arena (se actualizarán en update)
  }

  private getArenaGroundColor(): number {
    const map: Record<string, number> = {
      clasica: 0x2c3e50,
      hielo: 0xd6eaf8,
      lava: 0x641e16,
      electro: 0x1a1a2e,
      laberinto: 0x2c3e50,
      ventosa: 0x0e6251,
      diana: 0x2c3e50,
      giratoria: 0x1a2530,
    };
    return map[this.arenaDef.id] || 0x2c3e50;
  }

  private getArenaBorderColor(): number {
    const map: Record<string, number> = {
      clasica: 0x7f8c8d,
      hielo: 0x85c1e9,
      lava: 0xe74c3c,
      electro: 0xf1c40f,
      laberinto: 0x8e44ad,
      ventosa: 0x1abc9c,
      diana: 0xe67e22,
      giratoria: 0x3498db,
    };
    return map[this.arenaDef.id] || 0x7f8c8d;
  }

  private createArenaWalls(): void {
    const cx = ARENA_CENTER_X;
    const cy = ARENA_CENTER_Y;
    const r = ARENA_RADIUS;

    if (this.arenaDef.shape === 'circle') {
      // Aproximar círculo con un polígono de 32 lados
      const walls: MatterJS.BodyType[] = [];
      const segments = 32;

      for (let i = 0; i < segments; i++) {
        const angle1 = (i / segments) * Math.PI * 2;
        const angle2 = ((i + 1) / segments) * Math.PI * 2;
        const midAngle = (angle1 + angle2) / 2;

        const x1 = cx + Math.cos(angle1) * r;
        const y1 = cy + Math.sin(angle1) * r;
        const x2 = cx + Math.cos(angle2) * r;
        const y2 = cy + Math.sin(angle2) * r;

        // Crear una pequeña pared entre cada par de puntos del borde
        const wallLength = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
        const wallAngle = Math.atan2(y2 - y1, x2 - x1);
        const wallX = (x1 + x2) / 2;
        const wallY = (y1 + y2) / 2;

        const wall = this.matter.add.rectangle(wallX, wallY, wallLength + 4, 12, {
          isStatic: true,
          angle: wallAngle,
          label: 'wall',
          collisionFilter: {
            category: CAT_ARENA_WALL,
            mask: CAT_BEYBLADE,
          },
          restitution: 0.4,
        });
        walls.push(wall);
      }
    } else if (this.arenaDef.shape === 'rectangle') {
      // Paredes rectangulares
      const w = r * 1.2;
      const h = r * 1.2;
      const thickness = 12;

      // Arriba
      this.matter.add.rectangle(cx, cy - h / 2, w, thickness, { isStatic: true, label: 'wall', collisionFilter: { category: CAT_ARENA_WALL, mask: CAT_BEYBLADE }, restitution: 0.4 });
      // Abajo
      this.matter.add.rectangle(cx, cy + h / 2, w, thickness, { isStatic: true, label: 'wall', collisionFilter: { category: CAT_ARENA_WALL, mask: CAT_BEYBLADE }, restitution: 0.4 });
      // Izquierda
      this.matter.add.rectangle(cx - w / 2, cy, thickness, h, { isStatic: true, label: 'wall', collisionFilter: { category: CAT_ARENA_WALL, mask: CAT_BEYBLADE }, restitution: 0.4 });
      // Derecha
      this.matter.add.rectangle(cx + w / 2, cy, thickness, h, { isStatic: true, label: 'wall', collisionFilter: { category: CAT_ARENA_WALL, mask: CAT_BEYBLADE }, restitution: 0.4 });
    }
  }

  private createBeyblades(): void {
    // Aplicar fricción reducida si es arena Hielo
    const frictionMultiplier = this.arenaDef.id === 'hielo' ? this.arenaDef.friction : 1.0;
    const cx = ARENA_CENTER_X;
    const cy = ARENA_CENTER_Y;

    // Posiciones iniciales alrededor del centro
    const positions = this.getStartPositions();

    this.selections.forEach((sel, i) => {
      const classDef = CLASSES.find((c) => c.id === sel.className) || CLASSES[0];
      const specialDef = SPECIALS.find((s) => s.id === sel.specialName) || null;
      const pos = positions[i] || { x: cx + Math.random() * 100 - 50, y: cy + Math.random() * 100 - 50 };

      const beyblade = new Beyblade(this, i, classDef, specialDef, pos.x, pos.y);

      // Aplicar fricción reducida para arena Hielo
      if (this.arenaDef.id === 'hielo') {
        const originalFriction = (beyblade.body as any).friction || 0.8;
        this.beybladeOriginalFriction.set(beyblade, originalFriction);
        (beyblade.body as any).friction = originalFriction * this.arenaDef.friction;
      }

      this.players.push({
        beyblade,
        selection: sel,
        teamId: sel.teamId,
      });
    });
  }

  private getStartPositions(): { x: number; y: number }[] {
    const cx = ARENA_CENTER_X;
    const cy = ARENA_CENTER_Y;
    const count = this.selections.length; // usar selections, no players (aún vacío aquí)
    const minDist = 80; // distancia mínima entre peonzas
    const positions: { x: number; y: number }[] = [];

    // Distribuir en círculo con separación garantizada
    const spreadRadius = Math.min(ARENA_RADIUS * 0.45, (count * minDist) / (Math.PI * 2));

    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 - Math.PI / 2 + (Math.random() - 0.5) * 0.3;
      const r = spreadRadius * (0.8 + Math.random() * 0.4);
      const x = cx + Math.cos(angle) * r;
      const y = cy + Math.sin(angle) * r;
      positions.push({ x, y });
    }

    // Verificar distancias mínimas y recolocar si es necesario
    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        const dx = positions[i].x - positions[j].x;
        const dy = positions[i].y - positions[j].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < minDist) {
          // Separar: mover la segunda posición
          const angle = Math.atan2(dy, dx);
          const push = (minDist - dist) / 2;
          positions[j].x -= Math.cos(angle) * push;
          positions[j].y -= Math.sin(angle) * push;
          positions[i].x += Math.cos(angle) * push;
          positions[i].y += Math.sin(angle) * push;
        }
      }
    }

    return positions;
  }

  private createHUD(): void {
    this.hudGraphics = this.add.graphics();

    // Timer
    this.timerText = this.add.text(GAME_WIDTH / 2, 15, '0:00', {
      fontSize: '24px', color: '#ffffff',
    }).setOrigin(0.5);

    // Power-up spawn timer
    this.powerUpSpawnTimerText = this.add.text(GAME_WIDTH - 20, 15, '', {
      fontSize: '14px', color: '#f1c40f',
    }).setOrigin(1, 0);

    // Rank text
    this.rankText = this.add.text(GAME_WIDTH - 20, GAME_HEIGHT - 20, '', {
      fontSize: '14px', color: '#ffffff',
    }).setOrigin(1, 1);

    // Status texts para cada jugador
    this.statusTexts = [];
    this.players.forEach((p, i) => {
      const x = 10;
      const y = 50 + i * 30;

      const text = this.add.text(x, y, '', {
        fontSize: '13px',
        color: '#ffffff',
      });
      this.statusTexts.push(text);
    });
  }

  private setupCollisions(): void {
    this.matter.world.on('collisionstart', (event: any) => {
      for (const pair of event.pairs) {
        const bodyA = pair.bodyA;
        const bodyB = pair.bodyB;

        this.handleCollision(bodyA, bodyB);
      }
    });
  }

  private handleCollision(bodyA: MatterJS.BodyType, bodyB: MatterJS.BodyType): void {
    // Ignorar colisiones durante countdown o invulnerabilidad inicial
    if (this.phase !== 'fighting') return;
    if (this.invulnerabilityTimer > 0) return;

    // Identificar qué etiquetas tienen
    const labelA = bodyA.label || '';
    const labelB = bodyB.label || '';

    // Colisión peonza-peonza
    if (labelA.startsWith('beyblade_') && labelB.startsWith('beyblade_')) {
      const idxA = parseInt(labelA.replace('beyblade_', ''));
      const idxB = parseInt(labelB.replace('beyblade_', ''));

      // Matter.js ordena los pares por ID del cuerpo (el primero creado siempre es bodyA).
      // Esto hace que el jugador 0 (primero creado) siempre sea el "atacante" y nunca reciba daño.
      // Rotamos aleatoriamente para que ambos sufran y inflijan daño por igual.
      let finalIdxA = idxA;
      let finalIdxB = idxB;
      let finalBodyA = bodyA;
      let finalBodyB = bodyB;
      if (Math.random() < 0.5) {
        finalIdxA = idxB;
        finalIdxB = idxA;
        finalBodyA = bodyB;
        finalBodyB = bodyA;
      }

      // No pegar a miembros del mismo equipo en modo equipos
      if (this.config.modo === 'equipos') {
        if (this.players[finalIdxA].teamId === this.players[finalIdxB].teamId) return;
      }

      this.resolveBeybladeCollision(finalIdxA, finalIdxB, finalBodyA, finalBodyB);
    }

    // Colisión peonza-powerup
    if (labelA.startsWith('beyblade_') && labelB.startsWith('powerup_')) {
      const idxA = parseInt(labelA.replace('beyblade_', ''));
      const powerUp = this.powerUps.find((p) => p.body === bodyB);
      if (powerUp && !powerUp.collected) {
        this.applyPowerUp(idxA, powerUp);
      }
    }
    if (labelB.startsWith('beyblade_') && labelA.startsWith('powerup_')) {
      const idxB = parseInt(labelB.replace('beyblade_', ''));
      const powerUp = this.powerUps.find((p) => p.body === bodyA);
      if (powerUp && !powerUp.collected) {
        this.applyPowerUp(idxB, powerUp);
      }
    }

    // Colisión peonza-pared
    if (labelA.startsWith('beyblade_') && labelB === 'wall') {
      const idx = parseInt(labelA.replace('beyblade_', ''));
      this.onBeybladeWallCollision(idx);
    }
    if (labelB.startsWith('beyblade_') && labelA === 'wall') {
      const idx = parseInt(labelB.replace('beyblade_', ''));
      this.onBeybladeWallCollision(idx);
    }
  }

  private resolveBeybladeCollision(idxA: number, idxB: number, bodyA: MatterJS.BodyType, bodyB: MatterJS.BodyType): void {
    const pA = this.players[idxA];
    const pB = this.players[idxB];

    if (!pA.beyblade.isAlive() || !pB.beyblade.isAlive()) return;

    // Calcular velocidad relativa
    const velA = bodyA.velocity;
    const velB = bodyB.velocity;
    const relVelX = velA.x - velB.x;
    const relVelY = velA.y - velB.y;
    const relSpeed = Math.sqrt(relVelX * relVelX + relVelY * relVelY);

    // Daño base por velocidad relativa (mínimo 1 para evitar daño 0)
    const baseDamage = Math.max(1, Math.round(relSpeed * pA.beyblade.attack * 0.4));

    // Dirección del knockback (desde A hacia B) - segunda reducción
    const dx = bodyB.position.x - bodyA.position.x;
    const dy = bodyB.position.y - bodyA.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const kbForce = Math.min(relSpeed * 0.001, 0.01);

    const knockback = new Phaser.Math.Vector2(
      (dx / dist) * kbForce,
      (dy / dist) * kbForce
    );

    // Resetear contador de tiempo sin golpe del atacante
    pA.beyblade.timeWithoutHit = 0;

    // Aplicar daño a B
    const killed = pB.beyblade.applyDamage(baseDamage, knockback, idxA);

    // Vampiro: curar al atacante
    if (pA.beyblade.specialDef?.id === 'vampiro') {
      const healAmount = Math.round(baseDamage * 0.2);
      pA.beyblade.heal(healAmount);
    }

    // Elástico: devolver knockback
    if (pB.beyblade.specialDef?.id === 'elastico') {
      const returnKb = new Phaser.Math.Vector2(
        -knockback.x * 0.4,
        -knockback.y * 0.4
      );
      this.matter.body.applyForce(bodyA, bodyA.position, returnKb);
    }

    // Modo dopamina: puntos por golpe
    if (this.config.modoJuego === 'dopamina' && !killed) {
      pA.beyblade.points += DOPAMINE_POINTS_HIT;
      if (relSpeed > 2) {
        pA.beyblade.points += DOPAMINE_POINTS_CRITICAL;
      }
      // Combo
      pA.beyblade.timeWithoutHit = 0;
      pA.beyblade.combo++;
      if (pA.beyblade.combo >= 3) {
        pA.beyblade.points += DOPAMINE_POINTS_COMBO_BONUS;
      }
    }

    // Efecto visual de colisión
    this.spawnHitEffect(bodyB.position.x, bodyB.position.y, relSpeed);

    // Si mató, modo clásico - destrucción con efecto
    if (killed && this.config.modoJuego === 'clasico') {
      this.destroyBeyblade(pB.beyblade);
    }
  }

  private onBeybladeWallCollision(idx: number): void {
    const p = this.players[idx];
    if (!p.beyblade.isAlive()) return;

    // Modo dopamina: puntos por rebote contra pared
    if (this.config.modoJuego === 'dopamina') {
      p.beyblade.points += DOPAMINE_POINTS_WALL_BOUNCE;
    }

    // Efecto visual
    this.spawnWallHitEffect(p.beyblade.body.position.x, p.beyblade.body.position.y);
  }

  private applyPowerUp(playerIdx: number, powerUp: PowerUp): void {
    const p = this.players[playerIdx];
    if (!p.beyblade.isAlive()) return;
    if (p.beyblade.powerUpCooldown > 0) return;

    const def = powerUp.definition;
    powerUp.destroy();
    this.powerUps = this.powerUps.filter((pu) => pu !== powerUp);

    // Cooldown
    p.beyblade.powerUpCooldown = 3000;

    // Efecto visual de recogida
    this.spawnCollectEffect(p.beyblade.body.position.x, p.beyblade.body.position.y, def.color);

    switch (def.id) {
      case 'curación':
        p.beyblade.heal(Math.round(p.beyblade.maxHp * 0.25));
        break;

      case 'rafaga':
        this.addTimedEffect(p.beyblade, {
          id: 'rafaga',
          name: 'Ráfaga',
          remainingMs: def.duration,
          duration: def.duration,
          onUpdate: (b) => { b.speed = b.classDef.speed * 1.5; },
          onEnd: (b) => { b.speed = b.classDef.speed; },
        });
        break;

      case 'escudo':
        this.addTimedEffect(p.beyblade, {
          id: 'escudo',
          name: 'Escudo',
          remainingMs: def.duration,
          duration: def.duration,
        });
        break;

      case 'explosion': {
        // Dañar a todos los rivales cercanos
        const explosionRadius = 150;
        const explosionDamage = 15;
        this.players.forEach((other) => {
          if (other === p || !other.beyblade.isAlive()) return;
          const dx = other.beyblade.body.position.x - p.beyblade.body.position.x;
          const dy = other.beyblade.body.position.y - p.beyblade.body.position.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < explosionRadius) {
            const kbDir = dist > 0 ? { x: dx / dist, y: dy / dist } : { x: 0, y: -1 };
            const kb = new Phaser.Math.Vector2(kbDir.x * 0.015, kbDir.y * 0.015);
            other.beyblade.applyDamage(explosionDamage, kb, playerIdx);
          }
        });
        this.spawnExplosionEffect(p.beyblade.body.position.x, p.beyblade.body.position.y);
        break;
      }

      case 'furia':
        this.addTimedEffect(p.beyblade, {
          id: 'furia',
          name: 'Furia',
          remainingMs: def.duration,
          duration: def.duration,
          onUpdate: (b) => { b.attack = b.classDef.attack * 2; },
          onEnd: (b) => { b.attack = b.classDef.attack; },
        });
        break;

      case 'rayo': {
        // Encontrar rival más cercano
        let nearest: BattlePlayer | null = null;
        let minDist = Infinity;
        for (const other of this.players) {
          if (other === p || !other.beyblade.isAlive()) continue;
          const dx = other.beyblade.body.position.x - p.beyblade.body.position.x;
          const dy = other.beyblade.body.position.y - p.beyblade.body.position.y;
          const dist = dx * dx + dy * dy;
          if (dist < minDist) {
            minDist = dist;
            nearest = other;
          }
        }
        if (nearest) {
          nearest.beyblade.applyDamage(12, new Phaser.Math.Vector2(0, 0), playerIdx);
          const fromPos = new Phaser.Math.Vector2(p.beyblade.body.position.x, p.beyblade.body.position.y);
          const toPos = new Phaser.Math.Vector2(nearest.beyblade.body.position.x, nearest.beyblade.body.position.y);
          this.spawnLightningEffect(fromPos, toPos);
        }
        break;
      }

      case 'torbellino':
        this.addTimedEffect(p.beyblade, {
          id: 'torbellino',
          name: 'Torbellino',
          remainingMs: def.duration,
          duration: def.duration,
          onUpdate: (b, delta) => {
            // Atraer rivales
            this.players.forEach((other) => {
              if (other === p || !other.beyblade.isAlive()) return;
              const dx = b.body.position.x - other.beyblade.body.position.x;
              const dy = b.body.position.y - other.beyblade.body.position.y;
              const dist = Math.sqrt(dx * dx + dy * dy);
              if (dist < 130 && dist > 10) {
                const force = 0.0002;
                this.matter.body.applyForce(
                  other.beyblade.body,
                  other.beyblade.body.position,
                  { x: (dx / dist) * force, y: (dy / dist) * force }
                );
              }
            });
          },
        });
        break;

      case 'gigante':
        this.addTimedEffect(p.beyblade, {
          id: 'gigante',
          name: 'Gigante',
          remainingMs: def.duration,
          duration: def.duration,
          onUpdate: (b) => {
            // Escalar masa
            const currentMass = (b.body as any).mass || 1;
            if (currentMass < 0.03) {
              this.matter.body.setMass(b.body, currentMass * 2);
            }
          },
          onEnd: (b) => {
            this.matter.body.setMass(b.body, b.classDef.density * 100);
          },
        });
        break;
    }

    // Modo dopamina: puntos por recoger power-up
    if (this.config.modoJuego === 'dopamina') {
      p.beyblade.points += DOPAMINE_POINTS_POWERUP;
    }
  }

  private addTimedEffect(beyblade: Beyblade, effect: ActiveEffect): void {
    // Quitar efecto existente del mismo tipo
    beyblade.effects = beyblade.effects.filter((e) => e.id !== effect.id);
    beyblade.effects.push(effect);
  }

  private spawnHitEffect(x: number, y: number, intensity: number): void {
    const count = Math.min(Math.round(intensity * 3), 20);
    const g = this.add.graphics();
    const particles: { x: number; y: number; vx: number; vy: number; life: number; color: number }[] = [];

    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 1 + Math.random() * 3;
      particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 300 + Math.random() * 300,
        color: Math.random() > 0.5 ? 0xf1c40f : 0xffffff,
      });
    }

    const startTime = Date.now();
    const updateEvent = this.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        const elapsed = Date.now() - startTime;
        g.clear();
        let alive = false;

        particles.forEach((p) => {
          if (elapsed < p.life) {
            const progress = elapsed / p.life;
            p.x += p.vx;
            p.y += p.vy;
            p.vx *= 0.95;
            p.vy *= 0.95;
            g.fillStyle(p.color, 1 - progress);
            g.fillCircle(p.x, p.y, 2 * (1 - progress));
            alive = true;
          }
        });

        if (!alive) {
          updateEvent.destroy();
          g.destroy();
        }
      },
    });
  }

  private spawnExplosionEffect(x: number, y: number): void {
    this.cameras.main.shake(300, 0.01);

    const g = this.add.graphics();
    let radius = 0;
    const maxRadius = 80;
    const startTime = Date.now();

    this.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        const elapsed = Date.now() - startTime;
        if (elapsed > 400) {
          g.destroy();
          return;
        }
        g.clear();
        radius = (elapsed / 400) * maxRadius;
        const alpha = 1 - elapsed / 400;
        g.fillStyle(0xf1c40f, alpha * 0.3);
        g.fillCircle(x, y, radius);
        g.lineStyle(3, 0xff8800, alpha);
        g.strokeCircle(x, y, radius);
      },
    });
  }

  /** Destrucción completa de peonza con explosión y partículas */
  private destroyBeyblade(beyblade: Beyblade): void {
    const x = beyblade.body.position.x;
    const y = beyblade.body.position.y;
    const color = beyblade.color;

    // 1. Ocultar la peonza inmediatamente
    beyblade.graphics.setVisible(false);
    beyblade.status = 'eliminated';

    // 2. Cámara shake fuerte
    this.cameras.main.shake(500, 0.015);

    // 3. Destello blanco
    const flash = this.add.graphics();
    flash.fillStyle(0xffffff, 0.6);
    flash.fillCircle(x, y, 10);
    this.tweens.add({
      targets: flash,
      alpha: 0,
      scaleX: 8,
      scaleY: 8,
      duration: 400,
      ease: 'Quad.easeOut',
      onComplete: () => flash.destroy(),
    });

    // 4. Onda de choque expansiva
    const shockwave = this.add.graphics();
    let swRadius = 10;
    const startTime = Date.now();
    this.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        const elapsed = Date.now() - startTime;
        if (elapsed > 500) {
          shockwave.destroy();
          return;
        }
        swRadius += 2;
        const alpha = 1 - elapsed / 500;
        shockwave.clear();
        shockwave.lineStyle(4, color, alpha);
        shockwave.strokeCircle(x, y, swRadius);
        shockwave.lineStyle(2, 0xffffff, alpha * 0.5);
        shockwave.strokeCircle(x, y, swRadius * 0.7);
      },
    });

    // 5. Partículas de escombros
    const debrisCount = 20;
    const debris: { g: Phaser.GameObjects.Graphics; vx: number; vy: number; life: number }[] = [];
    for (let i = 0; i < debrisCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 2 + Math.random() * 4;
      const d = this.add.graphics();
      const size = 2 + Math.random() * 4;
      const debrisColor = Math.random() > 0.3 ? color : 0xf1c40f;
      d.fillStyle(debrisColor, 1);
      d.fillCircle(0, 0, size);
      d.setPosition(x, y);
      debris.push({
        g: d,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 600 + Math.random() * 400,
      });
    }

    const debrisStart = Date.now();
    this.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        const elapsed = Date.now() - debrisStart;
        let alive = false;
        debris.forEach((d) => {
          if (elapsed < d.life) {
            const progress = elapsed / d.life;
            d.g.x += d.vx;
            d.g.y += d.vy;
            d.vy += 0.05; // gravedad simulada
            d.g.setAlpha(1 - progress);
            d.g.setScale(1 - progress * 0.5);
            alive = true;
          } else {
            d.g.setVisible(false);
          }
        });
        if (!alive) {
          debris.forEach((d) => d.g.destroy());
        }
      },
    });

    // 6. Humo ascendente
    const smokeCount = 8;
    const smokeParticles: { g: Phaser.GameObjects.Graphics; vy: number; life: number }[] = [];
    for (let i = 0; i < smokeCount; i++) {
      const offsetX = (Math.random() - 0.5) * 30;
      const s = this.add.graphics();
      s.fillStyle(0x555555, 0.5);
      s.fillCircle(0, 0, 6 + Math.random() * 8);
      s.setPosition(x + offsetX, y);
      smokeParticles.push({
        g: s,
        vy: -(1 + Math.random() * 2),
        life: 800 + Math.random() * 400,
      });
    }

    const smokeStart = Date.now();
    this.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        const elapsed = Date.now() - smokeStart;
        let alive = false;
        smokeParticles.forEach((s) => {
          if (elapsed < s.life) {
            const progress = elapsed / s.life;
            s.g.y += s.vy;
            s.g.setAlpha(1 - progress);
            s.g.setScale(1 + progress);
            alive = true;
          } else {
            s.g.setVisible(false);
          }
        });
        if (!alive) {
          smokeParticles.forEach((s) => s.g.destroy());
        }
      },
    });

    // 7. Destruir el cuerpo físico después de la animación
    this.time.delayedCall(600, () => {
      beyblade.destroy();
    });
  }

  private spawnWallHitEffect(x: number, y: number): void {
    const g = this.add.graphics();
    g.fillStyle(0xffffff, 0.5);
    g.fillCircle(x, y, 8);
    this.time.delayedCall(200, () => g.destroy());
  }

  private spawnCollectEffect(x: number, y: number, color: number): void {
    const g = this.add.graphics();
    let radius = 10;
    const startTime = Date.now();

    this.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        const elapsed = Date.now() - startTime;
        if (elapsed > 300) {
          g.destroy();
          return;
        }
        g.clear();
        radius += 2;
        const alpha = 1 - elapsed / 300;
        g.fillStyle(color, alpha * 0.5);
        g.fillCircle(x, y, radius);
      },
    });
  }

  private spawnLightningEffect(from: Phaser.Math.Vector2, to: Phaser.Math.Vector2): void {
    const g = this.add.graphics();
    g.lineStyle(3, 0x4444ff, 0.8);
    g.beginPath();
    g.moveTo(from.x, from.y);

    // Línea con zigzag
    const steps = 5;
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const bx = from.x + (to.x - from.x) * t + (Math.random() - 0.5) * 30;
      const by = from.y + (to.y - from.y) * t + (Math.random() - 0.5) * 30;
      g.lineTo(bx, by);
    }
    g.strokePath();
    this.time.delayedCall(150, () => g.destroy());
  }

  private showCountdown(): void {
    const countdownText = this.add.text(ARENA_CENTER_X, ARENA_CENTER_Y, '3', {
      fontSize: '96px', color: '#ffffff',
    }).setOrigin(0.5);

    let count = 3;
    this.time.addEvent({
      delay: 1000,
      repeat: 3,
      callback: () => {
        count--;
        if (count > 0) {
          countdownText.setText(count.toString());
        } else if (count === 0) {
          countdownText.setText('¡YA!');
        } else {
          countdownText.destroy();
          this.phase = 'fighting';
        }
      },
    });
  }

  update(_time: number, delta: number): void {
    // Siempre mantener giro de peonzas, incluso en countdown
    this.players.forEach((p) => {
      if (p.beyblade.isAlive()) {
        this.matter.body.setAngularVelocity(p.beyblade.body, p.beyblade.speed);
      }
    });

    // IA de movimiento: dirección hacia rivales, power-ups, y cambios bruscos
    if (this.phase === 'fighting') {
      this.players.forEach((p) => {
        if (!p.beyblade.isAlive()) return;

        let targetX = 0;
        let targetY = 0;
        let foundTarget = false;

        // 1. Si lleva más de 4s sin golpear, cambio brusco de dirección
        if (p.beyblade.timeWithoutHit > 4000) {
          const angle = Math.random() * Math.PI * 2;
          const force = 0.0005; // fuerza fuerte para sacudida
          this.matter.body.applyForce(
            p.beyblade.body,
            p.beyblade.body.position,
            { x: Math.cos(angle) * force, y: Math.sin(angle) * force }
          );
          p.beyblade.timeWithoutHit = 2000; // reset parcial para no repetir inmediato
        }

        // 2. Buscar power-up cercano (30% de probabilidad si hay power-ups)
        if (this.powerUps.length > 0 && Math.random() < 0.3) {
          let nearestPu: PowerUp | null = null;
          let minPuDist = Infinity;
          for (const pu of this.powerUps) {
            if (pu.collected) continue;
            const dx = pu.body.position.x - p.beyblade.body.position.x;
            const dy = pu.body.position.y - p.beyblade.body.position.y;
            const dist = dx * dx + dy * dy;
            if (dist < minPuDist && dist < 200 * 200) { // solo si está relativamente cerca
              minPuDist = dist;
              nearestPu = pu;
            }
          }
          if (nearestPu) {
            targetX = nearestPu.body.position.x;
            targetY = nearestPu.body.position.y;
            foundTarget = true;
          }
        }

        // 3. Si no fue a por power-up, ir hacia rival más cercano (75% prob)
        if (!foundTarget && Math.random() < 0.75) {
          let nearest: BattlePlayer | null = null;
          let minDist = Infinity;
          for (const other of this.players) {
            if (other === p || !other.beyblade.isAlive()) continue;
            // No perseguir a compañeros de equipo
            if (this.config.modo === 'equipos' && p.teamId === other.teamId) continue;
            const dx = other.beyblade.body.position.x - p.beyblade.body.position.x;
            const dy = other.beyblade.body.position.y - p.beyblade.body.position.y;
            const dist = dx * dx + dy * dy;
            if (dist < minDist) {
              minDist = dist;
              nearest = other;
            }
          }
          if (nearest) {
            targetX = nearest.beyblade.body.position.x;
            targetY = nearest.beyblade.body.position.y;
            foundTarget = true;
          }
        }

        // Aplicar fuerza hacia el target
        if (foundTarget) {
          const dx = targetX - p.beyblade.body.position.x;
          const dy = targetY - p.beyblade.body.position.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 10) {
            this.matter.body.applyForce(
              p.beyblade.body,
              p.beyblade.body.position,
              { x: (dx / dist) * IA_DIRECTION_FORCE, y: (dy / dist) * IA_DIRECTION_FORCE }
            );
          }
        }
      });
    }

    if (this.phase === 'countdown' || this.phase === 'finished') return;

    this.battleTime += delta;

    // Timer de invulnerabilidad inicial
    if (this.invulnerabilityTimer > 0) {
      this.invulnerabilityTimer -= delta;
    }

    // Timer de arena Electro - alterna cada 8s
    if (this.arenaDef.id === 'electro') {
      this.electroTimer += delta;
      if (this.electroTimer >= 8000) {
        this.electroTimer = 0;
        this.electroPhase = this.electroPhase === 'attract' ? 'repel' : 'attract';
      }
    }

    // Actualizar peonzas
    this.players.forEach((p) => {
      if (p.beyblade.isAlive()) {
        p.beyblade.update(delta);

        // Efectos de arena Ventosa: succión al centro (reducido)
        if (this.arenaDef.id === 'ventosa') {
          const dx = ARENA_CENTER_X - p.beyblade.body.position.x;
          const dy = ARENA_CENTER_Y - p.beyblade.body.position.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 10) {
            const force = 0.00004;
            this.matter.body.applyForce(
              p.beyblade.body,
              p.beyblade.body.position,
              { x: (dx / dist) * force, y: (dy / dist) * force }
            );
          }
        }

        // Efectos de arena Giratoria
        if (this.arenaDef.id === 'giratoria') {
          const dx = p.beyblade.body.position.x - ARENA_CENTER_X;
          const dy = p.beyblade.body.position.y - ARENA_CENTER_Y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 10) {
            const tangentForce = 0.00006;
            this.matter.body.applyForce(
              p.beyblade.body,
              p.beyblade.body.position,
              { x: (-dy / dist) * tangentForce, y: (dx / dist) * tangentForce }
            );
          }
        }

        // Efectos de arena Electro - zonas magnéticas alternantes
        if (this.arenaDef.id === 'electro') {
          const dx = p.beyblade.body.position.x - ARENA_CENTER_X;
          const dy = p.beyblade.body.position.y - ARENA_CENTER_Y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          // 3 zonas radiales: interior (0-100), media (100-190), exterior (190-ARENA_RADIUS)
          const zoneRadius = ARENA_RADIUS;
          const innerZone = zoneRadius * 0.3;
          const midZone = zoneRadius * 0.6;

          let zoneForce = 0;
          if (dist < innerZone) {
            zoneForce = 0.00008; // zona interior: fuerza más intensa
          } else if (dist < midZone) {
            zoneForce = 0.00005; // zona media: fuerza media
          } else {
            zoneForce = 0.00003; // zona exterior: fuerza suave
          }

          if (dist > 10 && zoneForce > 0) {
            const dir = this.electroPhase === 'attract' ? -1 : 1;
            // En modo attract: fuerza hacia el centro, en repel: hacia fuera
            this.matter.body.applyForce(
              p.beyblade.body,
              p.beyblade.body.position,
              { x: (dx / dist) * zoneForce * dir, y: (dy / dist) * zoneForce * dir }
            );
          }
        }

        // Efectos de arena Lava (borde dañino) - no durante invulnerabilidad
        if (this.arenaDef.id === 'lava' && this.invulnerabilityTimer <= 0) {
          const dx = p.beyblade.body.position.x - ARENA_CENTER_X;
          const dy = p.beyblade.body.position.y - ARENA_CENTER_Y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > ARENA_RADIUS * 0.85) {
            p.beyblade.hp = Math.max(0, p.beyblade.hp - 0.001 * delta);
            if (p.beyblade.hp <= 0 && this.config.modoJuego === 'clasico') {
              this.destroyBeyblade(p.beyblade);
            }
          }
        }
      }
    });

    // Regeneración (especial) - también funciona durante invulnerabilidad
    this.players.forEach((p) => {
      if (!p.beyblade.isAlive()) return;
      if (p.beyblade.specialDef?.id === 'regeneracion' && p.beyblade.hp < p.beyblade.maxHp) {
        // Cada 3s
        const now = Date.now();
        const lastRegen = p.beyblade.specialCooldowns.get('regen') || 0;
        if (now - lastRegen >= 3000) {
          p.beyblade.specialCooldowns.set('regen', now);
          p.beyblade.heal(5);
        }
      }
    });

    // Veneno daño continuo - no durante invulnerabilidad
    if (this.invulnerabilityTimer <= 0) {
    this.players.forEach((p) => {
      if (!p.beyblade.isAlive() || p.beyblade.classDef.id !== 'veneno') return;
      this.players.forEach((other) => {
        if (other === p || !other.beyblade.isAlive()) return;
        const dx = other.beyblade.body.position.x - p.beyblade.body.position.x;
        const dy = other.beyblade.body.position.y - p.beyblade.body.position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 60) {
          other.beyblade.hp = Math.max(0, other.beyblade.hp - 0.0005 * delta);
          if (other.beyblade.hp <= 0 && this.config.modoJuego === 'clasico') {
            this.destroyBeyblade(other.beyblade);
          }
        }
      });
    });
    }

    // Magnético atracción
    this.players.forEach((p) => {
      if (!p.beyblade.isAlive() || p.beyblade.classDef.id !== 'magnetico') return;
      this.players.forEach((other) => {
        if (other === p || !other.beyblade.isAlive()) return;
        const dx = other.beyblade.body.position.x - p.beyblade.body.position.x;
        const dy = other.beyblade.body.position.y - p.beyblade.body.position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 100 && dist > 10) {
          const force = 0.0002;
          this.matter.body.applyForce(
            other.beyblade.body,
            other.beyblade.body.position,
            { x: (-dx / dist) * force, y: (-dy / dist) * force }
          );
        }
      });
    });

    // Ventoso desviación
    this.players.forEach((p) => {
      if (!p.beyblade.isAlive() || p.beyblade.classDef.id !== 'ventoso') return;
      this.players.forEach((other) => {
        if (other === p || !other.beyblade.isAlive()) return;
        const dx = other.beyblade.body.position.x - p.beyblade.body.position.x;
        const dy = other.beyblade.body.position.y - p.beyblade.body.position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 25 && dist > 5) {
          const force = 0.0004;
          this.matter.body.applyForce(
            other.beyblade.body,
            other.beyblade.body.position,
            { x: (dx / dist) * force, y: (dy / dist) * force }
          );
        }
      });
    });

    // Campo repulsivo (especial)
    this.players.forEach((p) => {
      if (!p.beyblade.isAlive() || p.beyblade.specialDef?.id !== 'campo_repulsivo') return;
      this.players.forEach((other) => {
        if (other === p || !other.beyblade.isAlive()) return;
        const dx = other.beyblade.body.position.x - p.beyblade.body.position.x;
        const dy = other.beyblade.body.position.y - p.beyblade.body.position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 80 && dist > 5) {
          const force = 0.0001;
          this.matter.body.applyForce(
            other.beyblade.body,
            other.beyblade.body.position,
            { x: (dx / dist) * force, y: (dy / dist) * force }
          );
        }
      });
    });

    // Garra magnética (especial) - atrae al centro cerca del borde
    this.players.forEach((p) => {
      if (!p.beyblade.isAlive() || p.beyblade.specialDef?.id !== 'garra_magnetica') return;
      const dx = p.beyblade.body.position.x - ARENA_CENTER_X;
      const dy = p.beyblade.body.position.y - ARENA_CENTER_Y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > ARENA_RADIUS - 40) {
        const force = 0.005;
        this.matter.body.applyForce(
          p.beyblade.body,
          p.beyblade.body.position,
          { x: (-dx / dist) * force, y: (-dy / dist) * force }
        );
      }
    });

    // Rastro de fuego (especial)
    this.players.forEach((p) => {
      if (!p.beyblade.isAlive() || p.beyblade.specialDef?.id !== 'rastro_fuego') return;
      // Dejar partículas en posición
      if (Math.random() < 0.1) {
        this.spawnFireTrail(p.beyblade.body.position.x, p.beyblade.body.position.y);
      }
    });

    // Check ring-out (modo clásico) - no durante invulnerabilidad
    if (this.config.modoJuego === 'clasico' && this.invulnerabilityTimer <= 0) {
      this.players.forEach((p) => {
        if (!p.beyblade.isAlive()) return;
        const dx = p.beyblade.body.position.x - ARENA_CENTER_X;
        const dy = p.beyblade.body.position.y - ARENA_CENTER_Y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > ARENA_RADIUS) {
          this.destroyBeyblade(p.beyblade);
        }
      });
    }

    // Visual de arena Electro - zonas magnéticas
    if (this.arenaDef.id === 'electro') {
      this.electroGraphics.clear();
      const color = this.electroPhase === 'attract' ? 0x00aaff : 0xff4444;
      const label = this.electroPhase === 'attract' ? 'ATRACCIÓN' : 'REPULSIÓN';
      const alpha = 0.15 + Math.sin(Date.now() * 0.003) * 0.08;

      // Zona interior
      this.electroGraphics.fillStyle(color, alpha);
      this.electroGraphics.fillCircle(ARENA_CENTER_X, ARENA_CENTER_Y, ARENA_RADIUS * 0.3);
      this.electroGraphics.lineStyle(3, color, alpha * 0.5);
      this.electroGraphics.strokeCircle(ARENA_CENTER_X, ARENA_CENTER_Y, ARENA_RADIUS * 0.6);
      this.electroGraphics.lineStyle(2, color, alpha * 0.3);
      this.electroGraphics.strokeCircle(ARENA_CENTER_X, ARENA_CENTER_Y, ARENA_RADIUS * 0.9);

      // Indicador de fase (crear una vez, actualizar siempre)
      if (!this.electroLabel) {
        this.electroLabel = this.add.text(ARENA_CENTER_X, 95, '', {
          fontSize: '16px', color: '#ffffff',
          fontStyle: 'bold', stroke: '#000000', strokeThickness: 3,
        }).setOrigin(0.5);
      }
      this.electroLabel.setText(`⚡ ${label}`);
      this.electroLabel.setColor(`#${color.toString(16).padStart(6, '0')}`);
    } else if (this.electroLabel) {
      this.electroLabel.setVisible(false);
    }

    // Modo dopamina: puntos por control del centro
    if (this.config.modoJuego === 'dopamina') {
      this.players.forEach((p) => {
        if (!p.beyblade.isAlive()) return;
        const dx = p.beyblade.body.position.x - ARENA_CENTER_X;
        const dy = p.beyblade.body.position.y - ARENA_CENTER_Y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        // Multiplicador según zona (solo en arena Diana hay 3 zonas con bonus)
        let multiplier = 1;
        if (this.arenaDef.id === 'diana') {
          const thirdR = ARENA_RADIUS / 3;
          if (dist < thirdR) {
            multiplier = 3; // zona centro: 3x
          } else if (dist < thirdR * 2) {
            multiplier = 2; // zona media: 2x
          } else {
            multiplier = 1; // zona exterior: 1x
          }
        } else {
          // En otras arenas solo puntúa la zona central
          if (dist >= DOPAMINE_CENTER_ZONE_RADIUS) return;
        }

        p.beyblade.points += DOPAMINE_POINTS_CENTER_CONTROL * multiplier * (delta / 1000);
      });
    }

    // Spawnear power-ups
    this.powerUpTimer -= delta;
    if (this.powerUpTimer <= 0) {
      this.powerUpTimer = POWERUP_SPAWN_INTERVAL;
      this.spawnPowerUp();
    }

    // Actualizar power-ups visuales
    this.powerUps.forEach((pu) => pu.update(delta));

    // Actualizar HUD
    this.updateHUD();

    // Verificar fin de partida
    this.checkBattleEnd();
  }

  private spawnFireTrail(x: number, y: number): void {
    const g = this.add.graphics();
    g.fillStyle(0xff6600, 0.5);
    g.fillCircle(x, y, 3 + Math.random() * 3);
    this.time.delayedCall(500, () => g.destroy());
  }

  private spawnPowerUp(): void {
    if (this.powerUps.length >= POWERUP_MAX_VISIBLE) return;

    const def = POWERUPS[Math.floor(Math.random() * POWERUPS.length)];
    const angle = Math.random() * Math.PI * 2;
    const dist = 50 + Math.random() * (ARENA_RADIUS - 80);

    const x = ARENA_CENTER_X + Math.cos(angle) * dist;
    const y = ARENA_CENTER_Y + Math.sin(angle) * dist;

    const powerUp = new PowerUp(this, x, y, def);
    this.powerUps.push(powerUp);
  }

  private updateHUD(): void {
    // Timer
    const maxTime = this.config.modoJuego === 'clasico' ? CLASSIC_TIMEOUT : DOPAMINE_TIMEOUT;
    const remaining = Math.max(0, maxTime - this.battleTime);
    const seconds = Math.ceil(remaining / 1000);
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    this.timerText.setText(`${min}:${sec.toString().padStart(2, '0')}`);

    // Color del timer según tiempo restante
    if (seconds < 30) {
      this.timerText.setColor('#e74c3c');
    } else if (seconds < 60) {
      this.timerText.setColor('#f1c40f');
    }

    // Indicador de invulnerabilidad inicial
    if (this.invulnerabilityTimer > 0) {
      const invSeconds = Math.ceil(this.invulnerabilityTimer / 1000);
      if (!this.protectionText) {
        this.protectionText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT * 0.35, '', {
          fontSize: '28px', color: '#00aaff', fontStyle: 'bold',
          stroke: '#000000', strokeThickness: 4,
        }).setOrigin(0.5);
      }
      this.protectionText.setText(`🛡️ PROTECCIÓN ${invSeconds}s`);
      this.protectionText.setAlpha(0.8 + Math.sin(Date.now() * 0.01) * 0.2);
      this.protectionText.setVisible(true);
    } else if (this.protectionText) {
      this.protectionText.setVisible(false);
    }

    // Power-up spawn countdown
    const spawnSec = Math.ceil(this.powerUpTimer / 1000);
    this.powerUpSpawnTimerText.setText(`Power-up: ${spawnSec}s`);

    // Status de jugadores
    const sortedPlayers = [...this.players];
    if (this.config.modoJuego === 'dopamina') {
      sortedPlayers.sort((a, b) => b.beyblade.points - a.beyblade.points);
    }

    sortedPlayers.forEach((p, i) => {
      const bey = p.beyblade;
      const hpPercent = (bey.hp / bey.maxHp) * 100;
      const statusStr = bey.status === 'eliminated' ? '💀 ELIMINADO' :
        `${'❤️'.repeat(Math.max(1, Math.round(hpPercent / 20)))} ${Math.round(hpPercent)}%`;

      const specialIcon = bey.specialDef ? SPECIALS.find((s) => s.id === bey.specialDef?.id)?.icon || '' : '';
      const puntosStr = this.config.modoJuego === 'dopamina' ? ` ★${Math.round(bey.points)}` : '';

      // Mostrar equipo si estamos en modo equipos
      const teamPrefix = this.config.modo === 'equipos'
        ? (p.teamId === 0 ? '🔵' : '🔴')
        : '';
      const playerNum = p.selection.isHuman ? `J${p.beyblade.playerIndex + 1}` : `IA${p.beyblade.playerIndex + 1}`;
      const text = `${teamPrefix} ${playerNum}: ${bey.classDef.name} ${specialIcon} ${statusStr}${puntosStr}`;

      if (this.statusTexts[i]) {
        this.statusTexts[i].setText(text);
        this.statusTexts[i].setColor(bey.status === 'eliminated' ? '#e74c3c' : '#ffffff');
      }
    });

    // Ranking en modo dopamina
    if (this.config.modoJuego === 'dopamina') {
      const ranking = sortedPlayers.map((p, i) =>
        `#${i + 1} J${p.selection.teamId + 1}${p.selection.isHuman ? '' : '(IA)'} — ${Math.round(p.beyblade.points)} pts`
      ).join('\n');
      this.rankText.setText(ranking);
    }

    // Dibujar barras de HP sobre peonzas + distintivos de equipo
    this.hudGraphics.clear();
    this.players.forEach((p) => {
      if (p.beyblade.status === 'eliminated') return;
      const bx = p.beyblade.body.position.x;
      const by = p.beyblade.body.position.y - p.beyblade.visualRadius - 12;
      const barW = 50;
      const barH = 6;
      const hpPct = p.beyblade.hp / p.beyblade.maxHp;

      // Distintivo de equipo (solo visible en modo equipos)
      if (this.config.modo === 'equipos') {
        const teamColors = [0x3498db, 0xe74c3c]; // Equipo 1 azul, Equipo 2 rojo
        const teamColor = teamColors[p.teamId % teamColors.length];
        // Bandera de equipo sobre la barra de HP
        this.hudGraphics.fillStyle(teamColor, 0.8);
        this.hudGraphics.fillTriangle(bx - 8, by - 10, bx + 8, by - 10, bx, by - 3);
        this.hudGraphics.fillStyle(0xffffff, 0.9);
        this.hudGraphics.fillCircle(bx, by - 10, 3);
      }

      // Fondo barra HP
      this.hudGraphics.fillStyle(0x000000, 0.6);
      this.hudGraphics.fillRect(bx - barW / 2, by, barW, barH);

      // Barra de HP
      const hpColor = hpPct > 0.5 ? 0x2ecc71 : hpPct > 0.25 ? 0xf1c40f : 0xe74c3c;
      this.hudGraphics.fillStyle(hpColor, 1);
      this.hudGraphics.fillRect(bx - barW / 2, by, barW * hpPct, barH);

      // Borde
      this.hudGraphics.lineStyle(1, 0xffffff, 0.5);
      this.hudGraphics.strokeRect(bx - barW / 2, by, barW, barH);

      // Icono de especial sobre la peonza
      if (p.beyblade.specialDef) {
        this.hudGraphics.fillStyle(0x9b59b6, 0.6);
        this.hudGraphics.fillCircle(bx, by - 10, 4);
      }

      // Escudo visual
      if (p.beyblade.effects.find((e) => e.id === 'escudo')) {
        this.hudGraphics.lineStyle(3, 0x00aaff, 0.5);
        this.hudGraphics.strokeCircle(bx, by + 10, p.beyblade.visualRadius + 5);
      }
    });
  }

  private checkBattleEnd(): void {
    if (this.battleFinished) return;

    const maxTime = this.config.modoJuego === 'clasico' ? CLASSIC_TIMEOUT : DOPAMINE_TIMEOUT;

    // Timeout
    if (this.battleTime >= maxTime) {
      this.finishBattle('timeout');
      return;
    }

    if (this.config.modoJuego === 'clasico') {
      const alive = this.players.filter((p) => p.beyblade.isAlive());

      if (this.config.modo === 'individual') {
        if (alive.length <= 1) {
          this.finishBattle('winner');
        }
      } else if (this.config.modo === 'equipos') {
        // Verificar si todos los miembros de un equipo han sido eliminados
        const teams = new Map<number, boolean>();
        this.players.forEach((p) => {
          if (!teams.has(p.teamId)) teams.set(p.teamId, true);
          if (p.beyblade.isAlive()) {
            teams.set(p.teamId, true);
          }
        });

        const aliveTeams = new Set<number>();
        this.players.forEach((p) => {
          if (p.beyblade.isAlive()) aliveTeams.add(p.teamId);
        });

        if (aliveTeams.size <= 1) {
          this.finishBattle('winner');
        }
      }
    }
  }

  private finishBattle(reason: string): void {
    this.battleFinished = true;
    this.phase = 'finished';

    // Determinar ganador
    let winnerIdx = -1;
    if (this.config.modo === 'equipos') {
      // En modo equipos, el ganador es un equipo completo
      const aliveTeams = new Set<number>();
      this.players.forEach((p) => {
        if (p.beyblade.isAlive()) aliveTeams.add(p.teamId);
      });
      if (aliveTeams.size === 1) {
        const winningTeam = aliveTeams.values().next().value as number;
        // Elegir al primer jugador vivo de ese equipo como representante
        const winner = this.players.find((p) => p.teamId === winningTeam && p.beyblade.isAlive());
        if (winner) winnerIdx = this.players.indexOf(winner);
      } else if (reason === 'timeout') {
        // Timeout: gana el equipo con más HP total
        let bestTeam = -1;
        let bestHp = 0;
        const teamHps = new Map<number, number>();
        this.players.forEach((p) => {
          const current = teamHps.get(p.teamId) || 0;
          teamHps.set(p.teamId, current + p.beyblade.hp);
        });
        teamHps.forEach((hp, team) => {
          if (hp > bestHp) { bestHp = hp; bestTeam = team; }
        });
        const winner = this.players.find((p) => p.teamId === bestTeam);
        if (winner) winnerIdx = this.players.indexOf(winner);
      }
    } else if (this.config.modoJuego === 'clasico') {
      // Individual clásico: último en pie
      const alive = this.players.filter((p) => p.beyblade.isAlive());
      if (alive.length === 1) {
        winnerIdx = this.players.indexOf(alive[0]);
      } else if (reason === 'timeout') {
        let maxHp = 0;
        this.players.forEach((p, i) => {
          if (p.beyblade.hp > maxHp) {
            maxHp = p.beyblade.hp;
            winnerIdx = i;
          }
        });
      }
    } else {
      // Modo dopamina individual: gana el que más puntos tenga
      let maxPoints = 0;
      this.players.forEach((p, i) => {
        if (p.beyblade.points > maxPoints) {
          maxPoints = p.beyblade.points;
          winnerIdx = i;
        }
      });
    }

    // Mostrar resultado
    this.showResult(winnerIdx);
  }

  private showResult(winnerIdx: number): void {
    // Overlay
    const overlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7);

    // Determinar texto del ganador según modo
    let winnerText: string;
    if (winnerIdx < 0) {
      winnerText = '🤝 ¡EMPATE!';
    } else if (this.config.modo === 'equipos') {
      const teamId = this.players[winnerIdx].teamId;
      const teamEmoji = teamId === 0 ? '🔵' : '🔴';
      winnerText = `🏆 ${teamEmoji} ¡EQUIPO ${teamId + 1} GANA! 🏆`;
    } else {
      winnerText = `🏆 ¡JUGADOR ${winnerIdx + 1} GANA! 🏆`;
    }

    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 80, winnerText, {
      fontSize: '42px',
      color: '#f1c40f',
      fontFamily: 'Arial Black, Arial',
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5);

    // Crear animación de entrada
    title.setScale(0);
    this.tweens.add({
      targets: title,
      scale: 1,
      duration: 500,
      ease: 'Back.easeOut',
    });

    // Resumen
    const lines: string[] = [];
    this.players.forEach((p, i) => {
      const cls = p.beyblade.classDef.name;
      if (this.config.modoJuego === 'dopamina') {
        lines.push(`J${i + 1} (${cls}): ${Math.round(p.beyblade.points)} pts`);
      } else {
        lines.push(`J${i + 1} (${cls}): ${p.beyblade.status === 'eliminated' ? '💀 Eliminado' : `❤️ ${Math.round(p.beyblade.hp)} HP`}`);
      }
    });

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 20, lines.join('\n'), {
      fontSize: '20px',
      color: '#ffffff',
      align: 'center',
      lineSpacing: 8,
    }).setOrigin(0.5);

    // Botón continuar
    const btnY = GAME_HEIGHT / 2 + 130;
    const btn = this.add.rectangle(GAME_WIDTH / 2, btnY, 250, 50, 0x27ae60)
      .setStrokeStyle(2, 0x2ecc71)
      .setInteractive({ useHandCursor: true });
    this.add.text(GAME_WIDTH / 2, btnY, 'CONTINUAR', {
      fontSize: '22px', color: '#ffffff',
    }).setOrigin(0.5);

    btn.on('pointerdown', () => {
      this.cleanup();
      const results: MatchResult[] = this.players.map((p) => ({
        playerIndex: p.beyblade.playerIndex,
        className: p.beyblade.classDef.id,
        specialName: p.selection.specialName,
        teamId: p.teamId,
        status: p.beyblade.status,
        points: Math.round(p.beyblade.points),
        hp: Math.round(p.beyblade.hp),
      }));

      this.scene.start('TorneoResultsScene', {
        config: this.config,
        selections: this.allSelections,
        arenaId: this.arenaId,
        results,
        winnerIdx,
        torneoSystem: this.torneoSystem,
        currentMatchData: this.currentMatchData,
      });
    });
  }

  private cleanup(): void {
    this.players.forEach((p) => p.beyblade.destroy());
    this.powerUps.forEach((pu) => pu.destroy());
  }
}
